import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  HStack,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import SingleDatePicker from "../../../../shared/components/DatePicker/SingleDatePicker";
import Select from "../../../../shared/components/Select";
import useApi from "../../../../shared/hooks/useApi";
import {
  CertificationPeriodId,
  ContractTypeId,
  PatientContractId,
  PatientDocumentTypeId,
  PatientId,
  PatientTaskTemplateId,
  PayrollCodeId,
  ServiceCodeId,
} from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import { getFullName } from "../../../../shared/utils/get-full-name";
import {
  BULK_SCHEDULE_DEFAULT_TASK_MINUTES,
  BulkScheduleCaregiver,
  BulkScheduleCertificationPeriod,
  BulkSchedulePatientContract,
  BulkScheduleTasksBody,
  DOCUMENT_TYPES_REQUIRING_CERTIFICATION_PERIOD,
  estimateBulkScheduleDays,
  NO_PAYROLL_CODE,
} from "../bulk-schedule-tasks.types";
import { RnSchedulingMapPatient } from "../rn-scheduling-map.types";
import BulkScheduleCaregiverSelect from "./BulkScheduleCaregiverSelect";
import CreateCertificationPeriodModal from "./CreateCertificationPeriodModal";

type Props = {
  isOpen: boolean;
  patients: RnSchedulingMapPatient[];
  onClose: () => void;
  onScheduled: () => void;
};

type FormErrors = Partial<
  Record<
    | "caregiver"
    | "taskTemplate"
    | "documents"
    | "title"
    | "scheduleDate"
    | "serviceCode"
    | "contracts"
    | "certificationPeriods",
    string
  >
>;

function isActiveContract(contract: BulkSchedulePatientContract, today: LocalDate) {
  return (
    contract.isOnHold !== true && (contract.endDate === null || !contract.endDate.isBefore(today))
  );
}

/**
 * The contract used for a patient when the coordinator did not pick one: the
 * only active contract, or the primary one.
 */
function getDefaultContractId(
  contracts: BulkSchedulePatientContract[] | undefined
): PatientContractId | null {
  if (contracts === undefined) {
    return null;
  }

  if (contracts.length === 1) {
    return contracts[0].id;
  }

  return contracts.find((contract) => contract.isPrimary === true)?.id ?? null;
}

function formatCertificationPeriod(period: BulkScheduleCertificationPeriod) {
  const type = period.type === "START_OF_CARE" ? "Start of care" : "Reassessment";

  return `${dateFormatter.toDate(period.startDate)} - ${dateFormatter.toDate(
    period.endDate
  )} (${type})`;
}

export default function BulkScheduleTasksModal(props: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const today = React.useMemo(() => LocalDate.now(), []);

  const [caregiver, setCaregiver] = React.useState<BulkScheduleCaregiver | null>(null);
  const [taskTemplateId, setTaskTemplateId] = React.useState<PatientTaskTemplateId | null>(null);
  const [documentIds, setDocumentIds] = React.useState<PatientDocumentTypeId[]>([]);
  const [title, setTitle] = React.useState("");
  const [scheduleDate, setScheduleDate] = React.useState<LocalDate | null>(null);
  const [serviceCodeId, setServiceCodeId] = React.useState<ServiceCodeId | null>(null);
  const [payrollCodeId, setPayrollCodeId] = React.useState<PayrollCodeId | null>(null);
  const [contractByPatient, setContractByPatient] = React.useState<
    Partial<Record<PatientId, PatientContractId>>
  >({});
  const [certificationPeriodByPatient, setCertificationPeriodByPatient] = React.useState<
    Partial<Record<PatientId, CertificationPeriodId>>
  >({});
  const [showErrors, setShowErrors] = React.useState(false);
  const [certificationPeriodPatient, setCertificationPeriodPatient] =
    React.useState<RnSchedulingMapPatient | null>(null);

  const taskTemplatesQuery = useQuery(queries.rnSchedulingMap.taskTemplates());
  const documentTypesQuery = useQuery(queries.rnSchedulingMap.documentTypes());
  const serviceCodesQuery = useQuery(queries.serviceCode.list());
  const payrollCodesQuery = useQuery(queries.rnSchedulingMap.payrollCodes());
  const agencySettingsQuery = useQuery(queries.rnSchedulingMap.agencySettings());

  const contractsQueries = useQueries({
    queries: props.patients.map((patient) => ({
      ...queries.rnSchedulingMap.patientContracts(patient.patientId),
      select: (data: { contracts: BulkSchedulePatientContract[] }) =>
        data.contracts.filter((contract) => isActiveContract(contract, today)),
    })),
  });

  const taskTemplates = taskTemplatesQuery.data ?? [];
  const documentTypes = React.useMemo(
    () => documentTypesQuery.data ?? [],
    [documentTypesQuery.data]
  );
  const serviceCodes = React.useMemo(
    () => serviceCodesQuery.data?.serviceCodes ?? [],
    [serviceCodesQuery.data]
  );
  const payrollCodes = React.useMemo(() => payrollCodesQuery.data ?? [], [payrollCodesQuery.data]);

  const selectedTemplate = taskTemplates.find((template) => template.id === taskTemplateId) ?? null;

  const documentTypeById = React.useMemo(
    () => new Map(documentTypes.map((document) => [document.id, document])),
    [documentTypes]
  );

  const isCms485Selected = documentIds.some((documentId) => {
    const documentType = documentTypeById.get(documentId);

    return (
      documentType !== undefined &&
      DOCUMENT_TYPES_REQUIRING_CERTIFICATION_PERIOD.includes(documentType.type)
    );
  });

  const isCertificationPeriodRequired =
    isCms485Selected && agencySettingsQuery.data?.requireCms485CertificationPeriod === true;

  const certificationPeriodsQueries = useQueries({
    queries: props.patients.map((patient) => ({
      ...queries.rnSchedulingMap.patientCertificationPeriods(patient.patientId),
      enabled: props.isOpen && isCms485Selected,
    })),
  });

  function getContractsOf(index: number) {
    return contractsQueries[index]?.data;
  }

  function getEffectiveContractId(patient: RnSchedulingMapPatient, index: number) {
    const contracts = getContractsOf(index);
    const chosen = contractByPatient[patient.patientId];

    if (chosen !== undefined && contracts?.some((contract) => contract.id === chosen) === true) {
      return chosen;
    }

    return getDefaultContractId(contracts);
  }

  function getEffectiveCertificationPeriodId(patient: RnSchedulingMapPatient, index: number) {
    const periods = certificationPeriodsQueries[index]?.data ?? [];
    const chosen = certificationPeriodByPatient[patient.patientId];

    if (chosen !== undefined && periods.some((period) => period.id === chosen)) {
      return chosen;
    }

    // Default to the period that put the patient on the map.
    return periods.some((period) => period.id === patient.certificationPeriodId)
      ? patient.certificationPeriodId
      : null;
  }

  const selectedContractTypeIds = React.useMemo(() => {
    const ids = new Set<ContractTypeId>();

    props.patients.forEach((patient, index) => {
      const contractId = getEffectiveContractId(patient, index);
      const contract = getContractsOf(index)?.find((c) => c.id === contractId);

      if (contract !== undefined) {
        ids.add(contract.contractTypeId);
      }
    });

    return [...ids].sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.patients, contractByPatient, contractsQueries.map((query) => query.data)]);

  const contractTypeServiceCodesQuery = useQuery(
    queries.rnSchedulingMap.contractTypeServiceCodes(selectedContractTypeIds)
  );

  // Same rule as the AngularJS contract-and-payment component: a service code
  // must have a billing rate for the contract type. Contract types without any
  // billing rates do not restrict the list.
  const serviceCodeOptions = React.useMemo(() => {
    const allowedByContractType = contractTypeServiceCodesQuery.data;

    return serviceCodes.filter((serviceCode) => {
      if (!serviceCode.isActive) {
        return false;
      }

      if (allowedByContractType === undefined) {
        return true;
      }

      return selectedContractTypeIds.every((contractTypeId) => {
        const allowed = allowedByContractType.get(contractTypeId);

        return allowed === undefined || allowed.has(serviceCode.id);
      });
    });
  }, [serviceCodes, contractTypeServiceCodesQuery.data, selectedContractTypeIds]);

  const effectiveServiceCode =
    serviceCodeOptions.find((serviceCode) => serviceCode.id === serviceCodeId) ?? null;

  const payrollCodeOptions = React.useMemo(() => {
    if (effectiveServiceCode === null) {
      return [];
    }

    return payrollCodes.filter(
      (payrollCode) =>
        payrollCode.isActive && effectiveServiceCode.payrollCodes.includes(payrollCode.id)
    );
  }, [payrollCodes, effectiveServiceCode]);

  const effectivePayrollCodeId =
    payrollCodeId !== null && payrollCodeOptions.some((code) => code.id === payrollCodeId)
      ? payrollCodeId
      : payrollCodeOptions.length === 1
      ? payrollCodeOptions[0].id
      : null;

  const durationMinutes = selectedTemplate?.durationMinutes ?? BULK_SCHEDULE_DEFAULT_TASK_MINUTES;
  const estimatedDays = estimateBulkScheduleDays(props.patients.length, durationMinutes);

  const errors: FormErrors = {};

  if (caregiver === null) {
    errors.caregiver = "Select the caregiver to assign";
  }

  if (selectedTemplate === null) {
    errors.taskTemplate = "Select a task template";
  }

  if (documentIds.length === 0 && selectedTemplate?.hasPlanOfCare !== true) {
    errors.documents = "Select at least one document";
  }

  if (title.trim() === "") {
    errors.title = "Enter a task name";
  }

  if (scheduleDate === null) {
    errors.scheduleDate = "Pick the date to start scheduling from";
  }

  if (effectiveServiceCode === null) {
    errors.serviceCode = "Select a service code";
  }

  const patientsWithoutContract = props.patients.filter(
    (patient, index) => getEffectiveContractId(patient, index) === null
  );

  if (patientsWithoutContract.length > 0) {
    errors.contracts = "Every patient needs a contract";
  }

  const patientsWithoutCertificationPeriod = isCertificationPeriodRequired
    ? props.patients.filter(
        (patient, index) => getEffectiveCertificationPeriodId(patient, index) === null
      )
    : [];

  if (patientsWithoutCertificationPeriod.length > 0) {
    errors.certificationPeriods =
      "Please select the certification period for the document for every patient";
  }

  const hasErrors = Object.keys(errors).length > 0;

  const scheduleMutation = useMutation({
    ...mutations.rnSchedulingMap.bulkScheduleTasks(),
    onSuccess: (response) => {
      toast({
        title: "Tasks scheduled",
        description: `${response.tasks.length} tasks were scheduled for ${getFullName(
          caregiver ?? { firstName: "the", lastName: "caregiver" }
        )}`,
        status: "success",
        position: "top",
      });
      queryClient.invalidateQueries({ queryKey: ["rnSchedulingMap", "patients"] });
      props.onScheduled();
      handleClose();
    },
    onError: (error) => {
      toast({
        title: "Failed to schedule tasks",
        description: formatErrorResponse(error),
        status: "error",
        duration: 10000,
        isClosable: true,
        position: "top",
      });
    },
  });

  /**
   * A period created from the "Create new" button is pushed straight into the
   * cached list and selected, so the row does not flash back to the default
   * while the list refetches.
   */
  function handleCertificationPeriodCreated(
    patientId: PatientId,
    period: BulkScheduleCertificationPeriod
  ) {
    queryClient.setQueryData(
      queries.rnSchedulingMap.patientCertificationPeriods(patientId).queryKey,
      (old) =>
        old === undefined
          ? old
          : { ...old, certificationPeriods: [...old.certificationPeriods, period] }
    );
    setCertificationPeriodByPatient((prev) => ({ ...prev, [patientId]: period.id }));
  }

  function resetForm() {
    setCaregiver(null);
    setTaskTemplateId(null);
    setDocumentIds([]);
    setTitle("");
    setScheduleDate(null);
    setServiceCodeId(null);
    setPayrollCodeId(null);
    setContractByPatient({});
    setCertificationPeriodByPatient({});
    setCertificationPeriodPatient(null);
    setShowErrors(false);
  }

  function handleClose() {
    resetForm();
    props.onClose();
  }

  function handleTaskTemplateChange(templateId: PatientTaskTemplateId | undefined) {
    const template = taskTemplates.find((t) => t.id === templateId) ?? null;

    setTaskTemplateId(template?.id ?? null);

    if (template === null) {
      setDocumentIds([]);
      return;
    }

    setDocumentIds(template.documents.filter((documentId) => documentTypeById.has(documentId)));
    setTitle(template.title);
  }

  function handleSave() {
    if (hasErrors || caregiver === null || selectedTemplate === null || scheduleDate === null) {
      setShowErrors(true);
      return;
    }

    if (effectiveServiceCode === null) {
      setShowErrors(true);
      return;
    }

    const body: BulkScheduleTasksBody = {
      caregiverId: caregiver.id,
      taskTemplateId: selectedTemplate.id,
      title: title.trim(),
      serviceCodeId: effectiveServiceCode.id,
      payrollCodeId: effectivePayrollCodeId,
      documents: documentIds,
      scheduleDate,
      patients: props.patients.map((patient, index) => {
        const patientContractId = getEffectiveContractId(patient, index);

        if (patientContractId === null) {
          throw new Error(`Missing contract for patient ${patient.patientId}`);
        }

        return {
          patientId: patient.patientId,
          patientContractId,
          certificationPeriodId: isCms485Selected
            ? getEffectiveCertificationPeriodId(patient, index)
            : null,
        };
      }),
    };

    scheduleMutation.mutate(body);
  }

  const isLoadingOptions =
    taskTemplatesQuery.isPending ||
    documentTypesQuery.isPending ||
    serviceCodesQuery.isPending ||
    payrollCodesQuery.isPending;

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={props.isOpen}
      scrollBehavior="inside"
      size="3xl"
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Schedule task for {props.patients.length} selected patients</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={5}>
            <Alert borderRadius="md" fontSize="sm" status="info">
              <AlertIcon />
              One task per patient will be scheduled with the selected caregiver, filling the free
              time between 09:00 and 18:00 from the schedule date onwards and continuing on the
              following days when a day is full.
            </Alert>

            <FormControl isRequired isInvalid={showErrors && errors.caregiver !== undefined}>
              <FormLabel>Caregiver</FormLabel>
              <BulkScheduleCaregiverSelect
                isInvalid={showErrors && errors.caregiver !== undefined}
                value={caregiver}
                onChange={setCaregiver}
              />
              <FormErrorMessage>{errors.caregiver}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={showErrors && errors.contracts !== undefined}>
              <FormLabel>Contract</FormLabel>
              <TableContainer borderRadius="md" borderWidth="1px">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Patient</Th>
                      <Th>Contract</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {props.patients.map((patient, index) => {
                      const contractsQuery = contractsQueries[index];
                      const contracts = contractsQuery?.data ?? [];
                      const contractId = getEffectiveContractId(patient, index);

                      return (
                        <Tr key={patient.patientId}>
                          <Td>
                            <Text fontSize="sm" fontWeight="medium">
                              {getFullName(patient)}
                            </Text>
                            <Text color="gray.500" fontSize="xs">
                              {patient.officeName}
                            </Text>
                          </Td>
                          <Td>
                            {(() => {
                              if (contractsQuery?.isPending === true) {
                                return (
                                  <Text color="gray.500" fontSize="xs">
                                    Loading contracts...
                                  </Text>
                                );
                              }

                              if (contracts.length === 0) {
                                return (
                                  <Text color="red.500" fontSize="xs">
                                    No active contract
                                  </Text>
                                );
                              }

                              return (
                                <Select
                                  label="Contract"
                                  multiple={false}
                                  options={contracts.map((contract) => ({
                                    value: contract.id,
                                    label: `${contract.contractTypeName} (${contract.payerName})`,
                                  }))}
                                  size="sm"
                                  value={contractId}
                                  onChange={(value) =>
                                    setContractByPatient((prev) => ({
                                      ...prev,
                                      [patient.patientId]: value,
                                    }))
                                  }
                                />
                              );
                            })()}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
              <FormErrorMessage>{errors.contracts}</FormErrorMessage>
            </FormControl>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired isInvalid={showErrors && errors.serviceCode !== undefined}>
                <FormLabel>Service code</FormLabel>
                <Select
                  isLoading={
                    serviceCodesQuery.isPending || contractTypeServiceCodesQuery.isFetching
                  }
                  label="Service code"
                  multiple={false}
                  options={serviceCodeOptions.map((serviceCode) => ({
                    value: serviceCode.id,
                    label: serviceCode.code,
                  }))}
                  value={effectiveServiceCode?.id ?? null}
                  onChange={(value) => {
                    setServiceCodeId(value ?? null);
                    setPayrollCodeId(null);
                  }}
                />
                {selectedContractTypeIds.length > 0 && (
                  <FormHelperText>
                    Filtered by the billing rates of the selected contracts
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.serviceCode}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Payroll code</FormLabel>
                <Select
                  isDisabled={effectiveServiceCode === null}
                  isLoading={payrollCodesQuery.isPending}
                  label="Payroll code"
                  multiple={false}
                  options={[
                    { value: NO_PAYROLL_CODE, label: "Empty (no code)" },
                    ...payrollCodeOptions.map((payrollCode) => ({
                      value: payrollCode.id as PayrollCodeId | typeof NO_PAYROLL_CODE,
                      label: payrollCode.displayId,
                    })),
                  ]}
                  value={effectivePayrollCodeId ?? NO_PAYROLL_CODE}
                  onChange={(value) =>
                    setPayrollCodeId(
                      value === undefined || typeof value === "string" ? null : value
                    )
                  }
                />
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired isInvalid={showErrors && errors.taskTemplate !== undefined}>
              <FormLabel>Task template</FormLabel>
              <Select
                isLoading={taskTemplatesQuery.isPending}
                label="Task template"
                multiple={false}
                options={taskTemplates.map((template) => ({
                  value: template.id,
                  label: template.title,
                }))}
                value={taskTemplateId}
                onChange={handleTaskTemplateChange}
              />
              {selectedTemplate !== null && (
                <FormHelperText>
                  Each task takes {durationMinutes} minutes. {props.patients.length} tasks will span
                  about {estimatedDays} {estimatedDays === 1 ? "day" : "days"}.
                </FormHelperText>
              )}
              <FormErrorMessage>{errors.taskTemplate}</FormErrorMessage>
            </FormControl>

            <FormControl
              isRequired
              isDisabled={selectedTemplate === null}
              isInvalid={showErrors && errors.documents !== undefined}
            >
              <FormLabel>Documents</FormLabel>
              <Select
                isDisabled={selectedTemplate === null}
                isLoading={documentTypesQuery.isPending}
                label="Documents"
                multiple={true}
                options={documentTypes.map((document) => ({
                  value: document.id,
                  label: document.title,
                }))}
                searchable={true}
                value={documentIds.length > 0 ? documentIds : null}
                onChange={(value) => setDocumentIds(value ?? [])}
              />
              <FormErrorMessage>{errors.documents}</FormErrorMessage>
            </FormControl>

            {isCms485Selected && (
              <FormControl
                isInvalid={showErrors && errors.certificationPeriods !== undefined}
                isRequired={isCertificationPeriodRequired}
              >
                <FormLabel>Certification period</FormLabel>
                <FormHelperText mb={2} mt={0}>
                  The selected documents are bound to a certification period. Confirm the period for
                  each patient.
                </FormHelperText>
                <TableContainer borderRadius="md" borderWidth="1px">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Patient</Th>
                        <Th>Certification period</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {props.patients.map((patient, index) => {
                        const periodsQuery = certificationPeriodsQueries[index];
                        const periods = periodsQuery?.data ?? [];

                        return (
                          <Tr key={patient.patientId}>
                            <Td>
                              <Text fontSize="sm" fontWeight="medium">
                                {getFullName(patient)}
                              </Text>
                            </Td>
                            <Td>
                              <HStack spacing={3}>
                                {(() => {
                                  if (periodsQuery?.isPending === true) {
                                    return (
                                      <Text color="gray.500" fontSize="xs">
                                        Loading certification periods...
                                      </Text>
                                    );
                                  }

                                  if (periods.length === 0) {
                                    return (
                                      <Text color="red.500" fontSize="xs">
                                        No certification periods
                                      </Text>
                                    );
                                  }

                                  return (
                                    <Select
                                      label="Certification period"
                                      multiple={false}
                                      options={periods.map((period) => ({
                                        value: period.id,
                                        label: formatCertificationPeriod(period),
                                      }))}
                                      size="sm"
                                      value={getEffectiveCertificationPeriodId(patient, index)}
                                      onChange={(value) =>
                                        setCertificationPeriodByPatient((prev) => ({
                                          ...prev,
                                          [patient.patientId]: value,
                                        }))
                                      }
                                    />
                                  );
                                })()}
                                <Button
                                  flexShrink={0}
                                  isDisabled={periodsQuery?.isPending === true}
                                  size="xs"
                                  variant="link"
                                  onClick={() => setCertificationPeriodPatient(patient)}
                                >
                                  Create new
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
                <FormErrorMessage>{errors.certificationPeriods}</FormErrorMessage>
              </FormControl>
            )}

            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired isInvalid={showErrors && errors.title !== undefined}>
                <FormLabel>Task name</FormLabel>
                <Input
                  placeholder="Enter task title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
                <FormErrorMessage>{errors.title}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={showErrors && errors.scheduleDate !== undefined}>
                <FormLabel>Schedule date</FormLabel>
                <SingleDatePicker
                  minDate={today.atStartOfDay()}
                  placeholderText="Start scheduling from"
                  selected={scheduleDate}
                  onChange={setScheduleDate}
                />
                <FormErrorMessage>{errors.scheduleDate}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </ModalBody>

        {certificationPeriodPatient !== null && (
          <CreateCertificationPeriodModal
            isOpen
            existingPeriods={
              certificationPeriodsQueries[
                props.patients.findIndex(
                  (patient) => patient.patientId === certificationPeriodPatient.patientId
                )
              ]?.data ?? []
            }
            patientId={certificationPeriodPatient.patientId}
            patientName={getFullName(certificationPeriodPatient)}
            onClose={() => setCertificationPeriodPatient(null)}
            onCreated={(period) =>
              handleCertificationPeriodCreated(certificationPeriodPatient.patientId, period)
            }
          />
        )}

        <ModalFooter>
          <Button
            isDisabled={scheduleMutation.isPending}
            mr={3}
            variant="ghost"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={isLoadingOptions || (showErrors && hasErrors)}
            isLoading={scheduleMutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
